// feat: persistencia offline de la caché de React Query en localStorage
//
// Solo se persisten lecturas (cursos, proyectos y empresas). Las
// escrituras offline de
// subrayados (cursos y podcasts) van por la cola IndexedDB de
// `web/src/lib/offline-queue.ts`, no por esta caché.
// `buster` invalida la caché persistida al cambiar de versión.
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import type { Query } from '@tanstack/react-query';

export const PERSIST_CACHE_KEY = 'xdface.rq-cache';
export const PERSIST_BUSTER = 'vault-pwa-v1';
export const PERSIST_MAX_AGE_MS = 1000 * 60 * 60 * 24; // 24 h

// Query keys de primer nivel que tienen sentido offline (lectura)
const PERSISTED_ROOT_KEYS = new Set(['courses', 'projects', 'companies']);

export function shouldDehydrateQuery(query: Query): boolean {
  if (query.state.status !== 'success') return false;
  const rootKey = String(query.queryKey[0] ?? '');
  return PERSISTED_ROOT_KEYS.has(rootKey);
}

export function createQueryPersister(userId: string, throttleTime = 1000) {
  const key = `${PERSIST_CACHE_KEY}:${encodeURIComponent(userId)}`;
  // Remove obsolete note data while preserving cached courses and project drafts.
  if (typeof window !== 'undefined') {
    try {
      const storage = window.localStorage;
      storage.removeItem('xdface.search-index');
      const raw = storage.getItem(key);
      if (raw) {
        const cache = JSON.parse(raw) as { clientState?: { queries?: { queryKey?: unknown[] }[] } };
        const queries = cache.clientState?.queries;
        if (Array.isArray(queries)) {
          const kept = queries.filter((query) => query.queryKey?.[0] !== 'notes');
          if (kept.length !== queries.length) {
            cache.clientState!.queries = kept;
            storage.setItem(key, JSON.stringify(cache));
          }
        }
      }
    } catch {
      // A blocked or invalid cache must never prevent the app from opening.
    }
  }
  return createSyncStoragePersister({
    storage: typeof window !== 'undefined' ? window.localStorage : undefined,
    key,
    throttleTime,
  });
}

export const persistOptions = {
  maxAge: PERSIST_MAX_AGE_MS,
  buster: PERSIST_BUSTER,
  dehydrateOptions: { shouldDehydrateQuery },
} as const;
